import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicApi } from '../services/api';
import toast from 'react-hot-toast';
import {
  Download, Upload, Send, CheckCircle2, FileText,
  ExternalLink, Check, Circle, ShieldCheck, Lock, Trash2,
  Shirt, Smartphone, BookOpen, ClipboardCheck, FileSignature,
  Building, AlertCircle, X, ChevronDown, ChevronUp
} from 'lucide-react';

// ═══════════════════════════════════════════════
// EXAM DATA — Manipulador de Alimentos
// ═══════════════════════════════════════════════
const EXAM1_QUESTIONS = [
  { section: 'Tipos de contaminación que existen', items: [
    { id: 1, text: 'Contaminación física, química y biológica', answer: true },
    { id: 2, text: 'Contaminación en línea y cruzada', answer: true },
    { id: 3, text: 'Contaminación física y teórica', answer: false },
  ]},
  { section: 'Los microorganismos son', items: [
    { id: 4, text: 'Seres vivos pequeños, sólo visibles al microscopio', answer: true },
    { id: 5, text: 'Órganos pequeños e internos de las bacterias', answer: false },
  ]},
  { section: 'La reproducción de las bacterias es', items: [
    { id: 6, text: 'Por fisión binaria', answer: true },
    { id: 7, text: 'Por gemación', answer: false },
  ]},
  { section: 'Factores que influyen en el crecimiento microbiano', items: [
    { id: 8, text: 'Oxígeno', answer: true },
    { id: 9, text: 'Olor', answer: false },
    { id: 10, text: 'Alimento', answer: true },
    { id: 11, text: 'Temperatura', answer: true },
    { id: 12, text: 'Acidez', answer: true },
    { id: 13, text: 'Humedad', answer: true },
  ]},
  { section: 'Más detalles sobre factores de crecimiento', items: [
    { id: 14, text: 'Entre los 10-50°C es la zona de máximo peligro ya que las bacterias se desarrollan con mucha facilidad', answer: false },
    { id: 15, text: 'Las bacterias que necesitan oxígeno para su crecimiento se llaman aéreas', answer: false },
    { id: 16, text: 'Al disminuir el agua disponible de un alimento, se dificulta el desarrollo de las bacterias', answer: true },
  ]},
  { section: '¿Cómo llegan los microorganismos al alimento?', items: [
    { id: 17, text: 'A través del aire', answer: true },
    { id: 18, text: 'A través de las manos', answer: true },
    { id: 19, text: 'Directamente al toser y estornudar', answer: true },
  ]},
  { section: 'Tipos de enfermedades alimentarias', items: [
    { id: 20, text: 'La intoxicación alimentaria es una alteración del alimento que lo hace desagradable', answer: false },
    { id: 21, text: 'La intoxicación alimentaria es una enfermedad provocada por una toxina que elabora un microorganismo', answer: true },
    { id: 22, text: 'La enfermedad producida por Staphylococcus aureus es una intoxicación', answer: true },
  ]},
  { section: 'Síntomas más frecuentes de enfermedades alimentarias', items: [
    { id: 23, text: 'Náuseas', answer: true },
    { id: 24, text: 'Vómitos', answer: true },
    { id: 25, text: 'Diarreas', answer: true },
    { id: 26, text: 'Dolor abdominal', answer: true },
    { id: 27, text: 'Artrosis', answer: false },
    { id: 28, text: 'Calambres', answer: true },
    { id: 29, text: 'Inflamación de las articulaciones', answer: false },
  ]},
  { section: 'Microorganismos que causan enfermedad', items: [
    { id: 30, text: 'El Staphylococcus aureus se localiza en la piel, nariz o garganta de muchas personas, incluso estando sanas', answer: true },
    { id: 31, text: 'El Staphylococcus aureus produce una toxina que puede provocar una intoxicación alimentaria', answer: true },
    { id: 32, text: 'El Staphylococcus aureus da lugar a una aureola o mancha reconocible a simple vista', answer: false },
    { id: 33, text: 'La Salmonella se llama así porque se localiza principalmente en el salmón ahumado', answer: false },
    { id: 34, text: 'La Salmonella se puede localizar en el intestino de animales y hombres infectados', answer: true },
    { id: 35, text: 'La Salmonella no produce toxinas', answer: true },
    { id: 36, text: 'El botulismo lo produce el Clostridium botulinum', answer: true },
    { id: 37, text: 'El Clostridium produce una toxina muy potente', answer: true },
    { id: 38, text: 'El Clostridium es capaz de esporular y aislarse de condiciones externas adversas', answer: true },
    { id: 39, text: 'El Clostridium se llama así por la costra que crea en la superficie donde se desarrolla', answer: false },
  ]},
  { section: 'Buenas Prácticas de Manipulación', items: [
    { id: 40, text: 'Las uñas deben ser cortas, estar limpias y sin pintar', answer: true },
    { id: 41, text: 'Si tienes heridas en las manos, no te las protejas para manipular alimentos', answer: false },
    { id: 42, text: 'Puedes ir a trabajar con el uniforme puesto desde tu casa', answer: false },
    { id: 43, text: 'Limpiarse las manos sobre el uniforme es un procedimiento correcto', answer: false },
    { id: 44, text: 'Cuantos más anillos lleves, más adecuado será para la manipulación', answer: false },
    { id: 45, text: 'Si estás en el frente y tienes que coger las patatas debes hacerlo por el envase, sin tocar el producto', answer: true },
    { id: 46, text: 'Los guantes desechables nos aíslan de todo tipo de contaminación, no es necesario cambiárselos', answer: false },
    { id: 47, text: 'No se debe mascar chicle ni comer en el puesto de trabajo', answer: true },
  ]},
  { section: 'Lavado de manos obligatorio después de', items: [
    { id: 48, text: 'Después de ir al baño', answer: true },
    { id: 49, text: 'Después de barrer', answer: true },
    { id: 50, text: 'Después de tocarse el pelo', answer: true },
    { id: 51, text: 'Después de limpiar o vaciar basuras', answer: true },
    { id: 52, text: 'Al empezar a trabajar', answer: true },
  ]},
  { section: 'Caducidades y rotación', items: [
    { id: 53, text: 'PEPS significa Primero Entra Primero Sale', answer: true },
    { id: 54, text: 'Caducidad primaria es la fecha que viene marcada en el envase del proveedor', answer: true },
    { id: 55, text: 'Caducidad secundaria es la que marcamos cuando variamos las condiciones de conservación', answer: true },
    { id: 56, text: 'Es importante que se use primero la última mercancía que ha llegado', answer: false },
  ]},
  { section: 'Métodos de conservación de alimentos', items: [
    { id: 57, text: 'Un alimento descongelado nunca puede volver a congelarse', answer: true },
    { id: 58, text: 'Las alteraciones químicas de los alimentos son producidas por microorganismos', answer: false },
    { id: 59, text: 'Los alimentos perecederos no necesitan condiciones especiales de conservación', answer: false },
    { id: 60, text: 'La refrigeración disminuye el crecimiento de la mayoría de los microorganismos', answer: true },
    { id: 61, text: 'La congelación detiene por completo la actividad y multiplicación de los microorganismos', answer: true },
    { id: 62, text: 'La pasteurización destruye todos los microorganismos incluidos los patógenos y sus formas de resistencia', answer: false },
    { id: 63, text: 'La desecación consiste en aumentar la vida útil disminuyendo el contenido en agua', answer: true },
  ]},
];

const EXAM2_QUESTIONS = [
  { section: 'APPCC', items: [
    { id: 1, text: 'El APPCC son las siglas de Análisis de Peligros y Puntos de Control Crítico', answer: true },
    { id: 2, text: 'El APPCC está compuesto por 10 pasos', answer: false },
    { id: 3, text: 'La LDCS significa Libro Diario de Calidad y Seguridad', answer: true },
  ]},
  { section: 'Limpieza y desinfección', items: [
    { id: 4, text: 'La desinfección supone una reducción en el número de microorganismos vivos', answer: true },
    { id: 5, text: 'La desinfección, para ser efectiva, debe ir acompañada de una limpieza previa', answer: true },
    { id: 6, text: 'Los utensilios no deben tener piezas de madera', answer: true },
    { id: 7, text: 'Para una correcta limpieza y desinfección no es importante la dosis del producto: ¡cuanto más, mejor!', answer: false },
  ]},
  { section: 'Control de plagas', items: [
    { id: 8, text: 'Este término se refiere a aplicar medidas para prevenir la entrada de insectos, ratones, etc.', answer: true },
  ]},
  { section: 'Buenas Prácticas de Manipulación', items: [
    { id: 9, text: 'Es importante colocar correctamente las carnes sobre la plancha', answer: true },
    { id: 10, text: 'Un buen mantenimiento y calibración de la maquinaria influye en la seguridad de la comida', answer: true },
    { id: 11, text: 'Almacenar los productos químicos alejados de los envases y alimentos', answer: true },
    { id: 12, text: 'Los cubos de basura deberán permanecer abiertos para evitar que el manipulador los abra con las manos', answer: false },
    { id: 13, text: 'Cubos de trapos: es fundamental respetar el código de colores establecidos', answer: true },
  ]},
  { section: 'Otros', items: [
    { id: 14, text: 'La trazabilidad es la manera de saber si existen restos (trazas) de un producto en una zona', answer: false },
  ]},
  { section: 'Etiquetado', items: [
    { id: 15, text: 'En el etiquetado debe figurar el modo de empleo cuando su omisión pueda provocar un uso incorrecto', answer: true },
    { id: 16, text: 'El lote no es necesario que figure en el etiquetado', answer: false },
  ]},
  { section: 'Nutrición', items: [
    { id: 17, text: 'Los hidratos de carbono son micronutrientes porque se encuentran en pequeñas cantidades', answer: false },
    { id: 18, text: 'Una de las funciones de las proteínas es reparación y renovación de los tejidos corporales', answer: true },
    { id: 19, text: 'La enfermedad celíaca es la intolerancia persistente al gluten', answer: true },
    { id: 20, text: 'La alergia alimentaria es una reacción adversa del sistema inmunológico ante un alimento', answer: true },
  ]},
];

// Capturas de las apps tal como aparecen en la tienda, para que el empleado
// reconozca cuál debe instalar.
import orquestShot from '../assets/orquest.png';
import mcdconnectShot from '../assets/mcdconnect.png';
import mcdconnectQr from '../assets/mcdconnect_qr.png';

// La PRL y el primer día forman un único paso. En el servidor el alta conserva
// sus ocho fases: la 7 y la 8 se muestran juntas como paso 7, y al finalizarlo
// se registra la fase 8, que es la que da el alta por completada.
const PHASE_LABELS = ['Datos', 'Uniformes', 'Accesos', 'Contrato', 'Documentación', 'Examen', 'Primer día'];
const ULTIMO_PASO = 7;

// Los pasos se agrupan según cuándo se realizan, para que el empleado vea
// de un vistazo qué le toca hacer desde casa y qué se hará presencialmente.
const PHASE_GROUPS = [
  { title: 'Desde casa', subtitle: 'Antes de la orientación', phases: [1, 2, 3, 4, 5] },
  { title: 'En la orientación', subtitle: 'Presencial, el martes', phases: [6] },
  { title: 'Después', subtitle: 'Antes de tu primer día', phases: [7] },
];

// Esta fase (el examen) se realiza presencialmente en la orientación.
const PRESENCIAL_FROM = 6;

// Cursos de Campus que el empleado debe completar antes de la orientación.
const CAMPUS_COURSES = [
  'Prevención de la violencia en el lugar de trabajo',
  'Hospitalidad',
  'Crear una cultura de pertenencia',
];

// Slug del enlace al formulario de vacaciones. La URL se edita desde el panel
// de administración (Enlaces), no está fijada en el código.
const VACATION_LINK_SLUG = 'vacaciones-forms';

// Guía para pedir el número de la Seguridad Social. Está en
// public/static/docs/: al compilar acaba en backend/staticfiles/docs/, que
// Nginx sirve en /static/. Fuera de /static/ la pediría a Django, que
// respondería con la página de la aplicación en lugar del PDF.
const GUIA_NUMERO_SS = `${process.env.PUBLIC_URL}/static/docs/guia-numero-seguridad-social.pdf`;

// Aplicaciones que el empleado debe instalar, con sus enlaces a las tiendas.
const APPS = [
  {
    key: 'orquest',
    name: 'Orquest',
    desc: 'Consulta tus turnos y ficha',
    shot: orquestShot,
    ios: 'https://apps.apple.com/es/app/orquest/id1453532559',
    android: 'https://play.google.com/store/apps/details?id=es.decide.mobile.orquest&hl=es',
  },
  {
    key: 'mcdconnect',
    name: 'McD Connect',
    desc: 'Comunicación interna',
    shot: mcdconnectShot,
    // Enlace oficial de McDonald's España, el mismo que contiene el QR de su
    // cartel. Sirve para iPhone y Android: en iPhone la app no aparece en la
    // App Store si no se llega a ella por este enlace.
    universal: 'http://api.joinblink.com/go/mcd-es',
    qr: mcdconnectQr,
    pasos: [
      <>Si te pide un dominio, escribe <strong>mcdonaldses</strong>.</>,
      <>Inicia sesión con tus credenciales <strong>CAM</strong> (tu ID de McDonald's) o
        {' '}<strong>GAM</strong> (tu correo ES-napellidos y tu contraseña de GAM).</>,
      <>Acepta los términos y condiciones.</>,
    ],
  },
];

// Documentación contractual que entrega gestoría y el empleado devuelve firmada.
// El contrato y el anexo de horas van en un único documento: son dos archivos
// a descargar, firmar y devolver, no tres. `aliases` incluye la etiqueta
// antigua para que las altas ya iniciadas sigan reconociéndose.
const CONTRACT_DOCS = [
  { aliases: ['contrato', 'anexo'], key: 'contract',
    label: 'Contrato y anexo de horas complementarias',
    signedLabel: 'Contrato y anexo firmados' },
  { aliases: ['145'], key: 'modelo145', label: 'Modelo 145 (IRPF)',
    signedLabel: 'Modelo 145 firmado' },
];

// Documentos del paso 1. La etiqueta con la que se guardan la pone el
// servidor; aquí solo se usa para saber si ya están subidos.
const DOCS_PASO1 = {
  identidad: { nombre: 'El DNI o NIE', falta: 'la copia de tu DNI o NIE por las dos caras',
    etiqueta: 'Documento de identidad (DNI o NIE)' },
  identidad_2: { nombre: 'La segunda cara del DNI o NIE',
    etiqueta: 'Documento de identidad (DNI o NIE), segunda cara' },
  titularidad: { nombre: 'El certificado de titularidad bancaria',
    falta: 'el certificado de titularidad de tu cuenta bancaria',
    etiqueta: 'Certificado de titularidad bancaria' },
};

// Aviso para las fases en las que se suben archivos. Los archivos abiertos
// directamente desde Google Drive u otras nubes son la causa más habitual de
// los fallos de subida en el móvil (ver handleFileChange).
function AvisoArchivos() {
  return (
    <div className="alert alert--info" style={{ marginBottom: 16, fontSize: '0.86rem' }}>
      <strong>Antes de subir un documento, guárdalo en tu teléfono.</strong> Al elegir el
      archivo, búscalo en «Archivos» o «Descargas», no en Google Drive ni en otras nubes:
      desde ahí la subida puede fallar.
    </div>
  );
}

// Aviso para la fase que se hace en la orientación (el examen).
function PresencialNotice() {
  return (
    <div className="alert alert--warning" style={{ marginBottom: 16, fontSize: '0.86rem' }}>
      <strong>Esta parte se hace presencialmente.</strong> Está pensada para completarse
      con nosotros durante la orientación del martes. Si has llegado hasta aquí desde
      casa, puedes detenerte: no hace falta que sigas por tu cuenta.
    </div>
  );
}

// ═══════════════════════════════════════════════
// EXAM COMPONENT
// ═══════════════════════════════════════════════
function ExamForm({ title, questions, requiredScore, totalQuestions, onSubmit, previousScore, previousPassed }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(previousPassed || false);
  const [result, setResult] = useState(previousPassed ? { score: previousScore, total: totalQuestions, passed: true } : null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const allItems = questions.flatMap(s => s.items);
  const toggleSection = (idx) => setExpandedSections(p => ({ ...p, [idx]: !p[idx] }));

  const handleAnswer = (id, val) => {
    if (submitted && result?.passed) return; // Can't change if passed
    setAnswers(a => ({ ...a, [id]: val }));
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length < allItems.length) {
      toast.error(`Responde todas las preguntas (${Object.keys(answers).length}/${allItems.length})`);
      return;
    }
    let correct = 0;
    allItems.forEach(item => {
      if (answers[item.id] === item.answer) correct++;
    });
    const passed = correct >= requiredScore;
    const res = { score: correct, total: totalQuestions, passed };
    setResult(res);
    setSubmitted(true);
    setShowFeedback(true);
    // Expand all sections to show errors
    const expanded = {};
    questions.forEach((_, i) => { expanded[i] = true; });
    setExpandedSections(expanded);
    if (passed) onSubmit(res);
  };

  const handleRetry = () => {
    // Keep answers, just allow editing and resubmit
    setSubmitted(false);
    setResult(null);
    setShowFeedback(false);
  };

  const isWrong = (item) => showFeedback && answers[item.id] !== undefined && answers[item.id] !== item.answer;
  const isCorrectAnswer = (item) => showFeedback && answers[item.id] !== undefined && answers[item.id] === item.answer;

  // Passed state
  if (submitted && result?.passed) {
    return (
      <div className="exam-result exam-result--passed">
        <div className="exam-result__icon">
          <CheckCircle2 size={40} />
        </div>
        <h3>¡Examen superado!</h3>
        <p><strong>{result.score}/{result.total}</strong> respuestas correctas ({Math.round(result.score / result.total * 100)}%)</p>
        <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
          Enhorabuena. Tu diploma se generará automáticamente.
        </p>
      </div>
    );
  }

  return (
    <div className="exam-form">
      <h4 className="exam-form__title">{title}</h4>
      <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: 16 }}>
        Marca <strong>V</strong> (Verdadero) o <strong>F</strong> (Falso) para cada afirmación.
        Necesitas {requiredScore}/{totalQuestions} correctas (95%) para aprobar.
      </p>

      {/* Failed banner */}
      {showFeedback && result && !result.passed && (
        <div className="exam-result exam-result--failed" style={{ marginBottom: 16, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertCircle size={24} />
            <div style={{ textAlign: 'left' }}>
              <strong>No superado: {result.score}/{result.total}</strong> ({Math.round(result.score / result.total * 100)}%)
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                Necesitas {requiredScore}. Las respuestas incorrectas están marcadas en rojo. Corrígelas y vuelve a enviar.
              </div>
            </div>
          </div>
        </div>
      )}

      {questions.map((section, si) => {
        const sectionErrors = showFeedback ? section.items.filter(i => answers[i.id] !== i.answer).length : 0;
        return (
          <div key={si} className="exam-section">
            <div className="exam-section__header" onClick={() => toggleSection(si)}>
              <span>{section.section}</span>
              <span style={{ fontSize: '0.8rem', color: sectionErrors > 0 ? 'var(--danger)' : 'var(--gray-400)' }}>
                {showFeedback && sectionErrors > 0 && `${sectionErrors} error${sectionErrors > 1 ? 'es' : ''} · `}
                {section.items.filter(i => answers[i.id] !== undefined).length}/{section.items.length}
                {expandedSections[si] === false ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
              </span>
            </div>
            {expandedSections[si] !== false && section.items.map(item => (
              <div key={item.id} className={`exam-question ${isWrong(item) ? 'exam-question--wrong' : ''} ${isCorrectAnswer(item) ? 'exam-question--correct' : ''}`}>
                <span className="exam-question__num">{item.id}.</span>
                <div style={{ flex: 1 }}>
                  <span className="exam-question__text">{item.text}</span>
                  {isWrong(item) && (
                    <div className="exam-feedback">
                      La respuesta correcta es <strong>{item.answer ? 'VERDADERO' : 'FALSO'}</strong>
                    </div>
                  )}
                </div>
                <div className="exam-question__btns">
                  <button
                    className={`exam-btn ${answers[item.id] === true ? (isWrong(item) ? 'exam-btn--selected exam-btn--wrong' : 'exam-btn--selected exam-btn--v') : ''}`}
                    onClick={() => handleAnswer(item.id, true)}
                    disabled={submitted && result?.passed}>V</button>
                  <button
                    className={`exam-btn ${answers[item.id] === false ? (isWrong(item) ? 'exam-btn--selected exam-btn--wrong' : 'exam-btn--selected exam-btn--f') : ''}`}
                    onClick={() => handleAnswer(item.id, false)}
                    disabled={submitted && result?.passed}>F</button>
                </div>
              </div>
            ))}
          </div>
        );
      })}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
          {Object.keys(answers).length}/{allItems.length} respondidas
        </span>
        {showFeedback && result && !result.passed ? (
          <button className="btn btn--primary" onClick={handleSubmit}>Corregir de nuevo</button>
        ) : (
          <button className="btn btn--primary" onClick={handleSubmit}>Corregir examen</button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════
export default function OnboardingPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [verified, setVerified] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  const [data, setData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [onboardingDocs, setOnboardingDocs] = useState([]);
  const [vacationFormUrl, setVacationFormUrl] = useState('');
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const [identity, setIdentity] = useState({ dni: '', social_security_number: '', iban: '' });
  const [accesses, setAccesses] = useState({
    access_uniformes: false, access_cam: false, access_orquest: false,
    vacation_form_done: false, courses_done: false,
  });
  const [uploadFiles, setUploadFiles] = useState({});
  // Archivos que se están leyendo en este momento (ver handleFileChange).
  const [leyendo, setLeyendo] = useState({});
  const [phase8, setPhase8] = useState({ guide_read: false });
  const [examResults, setExamResults] = useState({ exam1: null, exam2: null });

  // Signature
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  // La API devuelve ya las URLs absolutas de los archivos. No se antepone ningún
  // prefijo: hacerlo era la causa de que las descargas apuntaran a localhost en
  // producción, porque el valor quedaba incrustado en el bundle al compilar.
  const fileUrl = (path) => path || '';

  const loadDocs = useCallback(() => {
    publicApi.getOnboardingDocs(token)
      .then(d => setOnboardingDocs(d.data))
      .catch(() => setOnboardingDocs([]));
  }, [token]);

  useEffect(() => {
    publicApi.getContracts().catch(() => ({ data: [] })).then(c => setContracts(c.data));
    publicApi.getLinks()
      .then(res => {
        const link = (res.data || []).find(l => l.slug === VACATION_LINK_SLUG);
        // '#' es el marcador de "sin configurar" que usa el seed en otros enlaces.
        setVacationFormUrl(link && link.url && link.url !== '#' ? link.url : '');
      })
      .catch(() => setVacationFormUrl(''));
    loadDocs();
  }, [loadDocs]);

  // Filter docs for each phase.
  // Phase 5 = documents the employee fills & uploads (NOT diploma, exams, guide,
  // and NOT contract/anexo/modelo 145 which gestoría provides and live in phase 6).
  const docsForPhase5 = onboardingDocs.filter(d => {
    const n = d.name.toLowerCase();
    return d.doc_type === 'fill_upload' &&
      !n.includes('diploma') &&
      !n.includes('examen') &&
      !n.includes('guía') && !n.includes('guia') &&
      !n.includes('anexo') &&
      !n.includes('modelo 145') && !n.includes('145') &&
      !n.includes('contrato');
  });
  const downloadDocs = onboardingDocs.filter(d => {
    const n = d.name.toLowerCase();
    return d.doc_type === 'download' &&
      !n.includes('guía') && !n.includes('guia');
  });
  const guideDocs = onboardingDocs.filter(d =>
    d.name.toLowerCase().includes('guía') || d.name.toLowerCase().includes('guia')
  );

  // De más reciente a más antiguo: así `find` devuelve siempre la versión
  // vigente, también en las altas que tienen documentos repetidos de cuando
  // subir de nuevo no sustituía al anterior.
  const getUploadedFiles = () => [...(data?.files || [])].sort(
    (a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
  const isDocUploaded = (docId) => getUploadedFiles().some(f => f.document_type === docId);
  const getUploadedFile = (docId) => getUploadedFiles().find(f => f.document_type === docId);

  // ─── DNI Verification ─────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    if (!codeInput.trim()) { setError('Introduce tu contraseña de acceso.'); return; }
    setVerifying(true); setError('');
    try {
      const res = await publicApi.verifyOnboarding(token, codeInput.trim().toUpperCase());
      setData(res.data);
      setIdentity({
        dni: res.data.dni || '',
        social_security_number: res.data.social_security_number || '',
        iban: res.data.iban || '',
      });
      setAccesses({
        access_uniformes: res.data.access_uniformes,
        access_cam: res.data.access_cam,
        access_orquest: res.data.access_orquest,
        vacation_form_done: res.data.vacation_form_done,
        courses_done: res.data.courses_done,
      });
      setPhase8({ guide_read: res.data.guide_read });
      setExamResults({
        exam1: res.data.exam1_passed ? { score: res.data.exam1_score, total: 63, passed: true } : null,
        exam2: res.data.exam2_passed ? { score: res.data.exam2_score, total: 20, passed: true } : null,
      });
      setStep(Math.min(res.data.current_phase || 1, ULTIMO_PASO));
      setVerified(true);
      loadDocs();
      if (res.data.status === 'completed') setCompleted(true);
    } catch (err) { setError(err.response?.data?.error || 'Error al verificar.'); }
    setVerifying(false);
  };

  // ─── Phase update helper ──────────────────
  const updatePhase = async (payload) => {
    if (Object.keys(leyendo).length) {
      toast('Espera a que termine de prepararse el archivo.');
      return false;
    }
    setSubmitting(true);
    try {
      const isFormData = payload instanceof FormData;
      const code = codeInput.trim().toUpperCase();
      if (!isFormData) payload.access_code = code;
      else payload.append('access_code', code);
      const res = await publicApi.updateOnboarding(token, payload);
      setData(res.data);
      return true;
    } catch (err) {
      // Sin respuesta del servidor, la petición no llegó a completarse: casi
      // siempre es la conexión. Con respuesta, se muestra lo que dice.
      if (!err.response) {
        toast.error(
          'No se ha podido enviar. Comprueba tu conexión e inténtalo de nuevo. '
          + 'Si estás subiendo archivos, guárdalos antes en el teléfono.',
          { duration: 9000 });
      } else if (err.response.status === 413) {
        toast.error('Los archivos son demasiado grandes. Súbelos de uno en uno o con menos calidad.',
                    { duration: 9000 });
      } else {
        toast.error(err.response.data?.error || 'No se ha podido guardar. Inténtalo de nuevo.');
      }
      return false;
    }
    finally { setSubmitting(false); }
  };

  // Los archivos se leen enteros en el momento de elegirlos, no al enviar.
  //
  // En Android, un archivo elegido desde Google Drive u otra nube no llega al
  // navegador como tal: llega una referencia que hay que ir a buscar a la nube
  // en el momento de leerla. Si se leía al enviar y la conexión flaqueaba, la
  // lectura fallaba dentro del propio móvil y la petición nunca salía, por eso
  // el servidor no registraba ningún error. Leyéndolo aquí, se descarga en ese
  // momento; y si no se puede, el empleado lo sabe al instante.
  const MAX_MB = 20;

  const leerArchivo = (file) => (
    file.arrayBuffer
      ? file.arrayBuffer()
      : new Promise((ok, ko) => {
          const r = new FileReader();
          r.onload = () => ok(r.result);
          r.onerror = () => ko(r.error);
          r.readAsArrayBuffer(file);
        })
  );

  const quitarArchivo = (key) =>
    setUploadFiles(prev => { const n = { ...prev }; delete n[key]; return n; });

  const handleFileChange = async (key, file) => {
    if (!file) { quitarArchivo(key); return; }
    if (DOCS_PASO1[key] && !/\.pdf$/i.test(file.name || '')) {
      quitarArchivo(key);
      toast.error(`${DOCS_PASO1[key].nombre}: tiene que estar escaneado en PDF; no se admiten fotos. `
                  + 'Debajo de las subidas se explica cómo escanearlo con el móvil.', { duration: 9000 });
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      quitarArchivo(key);
      toast.error(`El archivo pesa más de ${MAX_MB} MB. Prueba a escanearlo con menos calidad.`,
                  { duration: 7000 });
      return;
    }
    setLeyendo(prev => ({ ...prev, [key]: true }));
    const aviso = toast.loading('Preparando el archivo...');
    try {
      const contenido = await leerArchivo(file);
      if (!contenido || !contenido.byteLength) throw new Error('archivo vacío');
      // Copia en memoria: a partir de aquí la subida ya no depende de la nube.
      const copia = new File([contenido], file.name,
                             { type: file.type || 'application/octet-stream' });
      setUploadFiles(prev => ({ ...prev, [key]: copia }));
      toast.dismiss(aviso);
    } catch {
      quitarArchivo(key);
      toast.error(
        'No se ha podido leer el archivo. Si está en Google Drive u otra nube, '
        + 'descárgalo primero al teléfono y vuelve a seleccionarlo desde «Archivos».',
        { id: aviso, duration: 9000 });
    } finally {
      setLeyendo(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  // ─── Signature ────────────────────────────
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 200;
    ctx.strokeStyle = '#1a365d'; ctx.lineWidth = 2; ctx.lineCap = 'round';
  }, []);

  useEffect(() => { if (step === 5 && verified) setTimeout(initCanvas, 100); }, [step, verified, initCanvas]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };
  const startDraw = (e) => { e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); setIsDrawing(true); };
  const draw = (e) => { if (!isDrawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasSigned(true); };
  const endDraw = () => setIsDrawing(false);
  const clearSignature = () => { const c = canvasRef.current; if (c) { c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasSigned(false); } };
  const getSignatureBlob = () => new Promise(r => canvasRef.current.toBlob(r, 'image/png'));

  // ─── Uploaded badge ───────────────────────
  const UploadedBadge = ({ docId }) => {
    const file = getUploadedFile(docId);
    if (!file) return null;
    return (
      <div className="uploaded-badge">
        <CheckCircle2 size={14} />
        <span>Subido: <a href={fileUrl(file.file)} target="_blank" rel="noreferrer">{file.label}</a></span>
        <span className="uploaded-badge__date">
          {new Date(file.uploaded_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
        </span>
      </div>
    );
  };

  // ─── Phase submits ────────────────────────
  // ── Fase 1 — Datos personales (los aporta el empleado) ──
  const docPaso1Subido = (key) => {
    if (key === 'identidad') return !!data?.has_identity_doc;
    if (key === 'titularidad') return !!data?.has_bank_cert;
    return (data?.files || []).some(f => f.label === DOCS_PASO1[key].etiqueta);
  };

  const submitPhase1 = async () => {
    if (!identity.dni.trim()) { toast.error('Introduce tu DNI / NIE.'); return; }
    if (!identity.social_security_number.trim()) { toast.error('Introduce tu número de la Seguridad Social.'); return; }
    if (!identity.iban.trim()) { toast.error('Introduce tu IBAN.'); return; }
    for (const key of ['identidad', 'titularidad']) {
      if (!uploadFiles[key] && !docPaso1Subido(key)) {
        toast.error(`Falta adjuntar ${DOCS_PASO1[key].falta}.`, { duration: 7000 });
        return;
      }
    }

    // Cada archivo va en su propia petición: los tres juntos podrían pasar
    // del límite del servidor. Los que ya han subido no se repiten si
    // falla uno posterior. La identidad va antes que la segunda cara, porque
    // un DNI nuevo en un solo archivo sustituye a la segunda cara anterior.
    for (const key of Object.keys(DOCS_PASO1)) {
      if (!uploadFiles[key]) continue;
      const fd = new FormData();
      fd.append(`file_${key}`, uploadFiles[key]);
      if (!(await updatePhase(fd))) return;
      quitarArchivo(key);
    }

    const formData = new FormData();
    formData.append('dni', identity.dni.trim());
    formData.append('social_security_number', identity.social_security_number.trim());
    formData.append('iban', identity.iban.trim());
    formData.append('current_phase', 2);
    if (await updatePhase(formData)) setStep(2);
  };

  // ── Fase 2 — Uniformes y preferencias de vacaciones ──
  const submitPhase2 = async () => {
    if (!accesses.access_uniformes) { toast.error('Confirma que has solicitado el uniforme.'); return; }
    if (await updatePhase({
      access_uniformes: true,
      vacation_form_done: accesses.vacation_form_done,
      current_phase: 3,
    })) setStep(3);
  };

  // ── Fase 3 — Accesos a plataformas y cursos previos ──
  const submitPhase3 = async () => {
    if (await updatePhase({
      access_cam: accesses.access_cam,
      access_orquest: accesses.access_orquest,
      courses_done: accesses.courses_done,
      current_phase: 4,
    })) setStep(4);
  };

  // ── Fase 4 — Contrato, anexo y Modelo 145 (los entrega gestoría) ──
  // No bloquea: la documentación de gestoría puede no estar lista todavía, y el
  // empleado debe poder seguir avanzando. Lo que falte queda reflejado en el
  // panel de gestoría y en el resumen final.
  const submitPhase4 = async () => {
    const formData = new FormData();
    formData.append('current_phase', 5);
    CONTRACT_DOCS.forEach(def => {
      if (uploadFiles[def.key]) {
        formData.append(`file_${def.key}`, uploadFiles[def.key]);
        formData.append(`label_file_${def.key}`, def.signedLabel);
      }
    });
    if (await updatePhase(formData)) setStep(5);
  };

  // ── Fase 5 — Documentación informativa y firma (presencial) ──
  const submitPhase5 = async () => {
    const missing = docsForPhase5.filter(d => !uploadFiles[`doc_${d.id}`] && !isDocUploaded(d.id));
    if (missing.length > 0) { toast.error(`Faltan: ${missing.map(d => d.name).join(', ')}`); return; }
    const formData = new FormData();
    formData.append('current_phase', 6);
    Object.keys(uploadFiles).forEach(key => {
      if (key.startsWith('doc_') && uploadFiles[key]) {
        formData.append(`file_${key}`, uploadFiles[key]);
        const docId = key.replace('doc_', '');
        const doc = onboardingDocs.find(d => d.id === parseInt(docId));
        formData.append(`label_file_${key}`, doc ? doc.name : `Doc ${docId}`);
        formData.append(`doc_type_file_${key}`, docId);
      }
    });
    if (hasSigned) {
      const blob = await getSignatureBlob();
      formData.append('file_firma_docs', blob, 'firma_documentacion.png');
      formData.append('label_file_firma_docs', 'Firma de documentación');
    }
    if (await updatePhase(formData)) setStep(6);
  };

  // ── Fase 6 — Examen de manipulador de alimentos (presencial) ──
  const submitPhase6 = async () => {
    if (!examResults.exam1?.passed && !data?.exam1_passed) { toast.error('Completa el Examen 1.'); return; }
    if (!examResults.exam2?.passed && !data?.exam2_passed) { toast.error('Completa el Examen 2.'); return; }
    const payload = { current_phase: 7 };
    if (examResults.exam1?.passed) {
      payload.exam1_score = examResults.exam1.score; payload.exam1_passed = true;
    }
    if (examResults.exam2?.passed) {
      payload.exam2_score = examResults.exam2.score; payload.exam2_passed = true;
    }
    if (await updatePhase(payload)) setStep(7);
  };

  const submitPhase8 = async () => {
    if (!phase8.guide_read) { toast.error('Confirma que has leído la guía.'); return; }
    if (await updatePhase({ guide_read: true, current_phase: 8 })) {
      setCompleted(true); toast.success('¡Proceso completado!');
    }
  };

  // ─── Summary ──────────────────────────────
  const SummaryChecklist = () => {
    const items = [
      { label: 'Datos personales, DNI, Seguridad Social e IBAN',
        done: !!(data?.iban && data?.dni && data?.social_security_number) },
      { label: 'Uniforme solicitado', done: data?.access_uniformes },
      { label: 'Accesos a plataformas (Orquest / Campus)', done: true },
      { label: 'Contrato y documentación firmados',
        done: (data?.signed_uploaded || []).length > 0,
        detail: (data?.signed_uploaded || []).length > 0
          ? `Entregado: ${(data.signed_uploaded || []).join(', ')}` : '' },
      { label: 'Documentación de la orientación firmada', done: step > 5 },
      { label: 'Examen manipulador', done: data?.exam1_passed && data?.exam2_passed,
        detail: data?.exam1_passed ? `Examen 1: ${data.exam1_score}/63 · Examen 2: ${data.exam2_score}/20` : '' },
      { label: 'Guía leída', done: phase8.guide_read },
    ];
    return (
      <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)', padding: 16, border: '1px solid var(--gray-200)', marginBottom: 20 }}>
        <div style={{ marginBottom: 12, fontSize: '0.92rem' }}>
          <strong>{data?.first_name} {data?.last_name}</strong> · {data?.restaurant_name}
        </div>
        {items.map((item, i) => (
          <div key={i} className="summary-item">
            <div className={`summary-icon ${item.done ? 'summary-icon--done' : ''}`}>
              {item.done ? <Check size={14} /> : <Circle size={14} />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: '0.88rem' }}>{item.label}</div>
              {item.detail && <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{item.detail}</div>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Completed ────────────────────────────
  if (completed) {
    return (
      <div className="new-employee-section">
        <div className="new-employee-card" style={{ textAlign: 'center', paddingTop: 60 }}>
          <CheckCircle2 size={64} color="var(--success)" style={{ marginBottom: 16 }} />
          <h2>Proceso completado</h2>
          <p style={{ color: 'var(--gray-500)', marginBottom: 32 }}>Toda tu documentación ha sido enviada. El equipo de administración la revisará.</p>
          <button onClick={() => navigate('/')} className="btn btn--primary">Ir al inicio</button>
        </div>
      </div>
    );
  }

  // ─── Acceso con contraseña temporal ───────
  if (!verified) {
    return (
      <div className="auth-page">
        <div className="auth-card animate-in" style={{ maxWidth: 420 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <ShieldCheck size={48} color="var(--blue-500)" style={{ marginBottom: 12 }} />
            <h2>Acceso a tu alta</h2>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem', marginTop: 8 }}>
              Introduce la contraseña temporal que te facilitamos junto a este enlace.
            </p>
          </div>
          <form onSubmit={handleVerify}>
            <div className="form-group">
              <label><Lock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Contraseña de acceso</label>
              <input className="form-input" value={codeInput}
                onChange={e => { setCodeInput(e.target.value.toUpperCase()); setError(''); }}
                placeholder="ABCD2345" autoFocus autoCapitalize="characters" autoCorrect="off" spellCheck="false"
                style={{ fontSize: '1.25rem', textAlign: 'center', letterSpacing: '0.15em' }} />
              {error && <div className="form-error">{error}</div>}
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 8, textAlign: 'center' }}>
                ¿No la tienes? Escríbenos y te la facilitamos de nuevo.
              </div>
            </div>
            <button type="submit" className="btn btn--primary btn--lg" disabled={verifying} style={{ width: '100%' }}>
              {verifying ? 'Verificando...' : 'Acceder'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Main flow ────────────────────────────
  return (
    <div className="new-employee-section">
      <div className="new-employee-card" style={{ maxWidth: 820 }}>
        {/* Progreso, agrupado por momento de realización */}
        <div className="onboarding-groups animate-in">
          {PHASE_GROUPS.map((group, gi) => (
            <div key={gi} className={`onboarding-group ${
              group.phases.includes(step) ? 'onboarding-group--current' : ''} ${
              step > group.phases[group.phases.length - 1] ? 'onboarding-group--done' : ''}`}>
              <div className="onboarding-group__head">
                <span className="onboarding-group__title">{group.title}</span>
                <span className="onboarding-group__subtitle">{group.subtitle}</span>
              </div>
              <div className="onboarding-group__steps">
                {group.phases.map(n => (
                  <div key={n}
                    className={`onboarding-step ${step >= n ? 'onboarding-step--active' : ''} ${step > n ? 'onboarding-step--completed' : ''} ${step === n ? 'onboarding-step--current' : ''}`}
                    onClick={() => { if (n < step) setStep(n); }}
                    style={{ cursor: n < step ? 'pointer' : 'default' }}>
                    <span className="onboarding-step__number">{step > n ? <Check size={14} /> : n}</span>
                    <span className="onboarding-step__label">{PHASE_LABELS[n - 1]}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Orientación a la que está citado, por si pierde el correo. Solo
            llega con el envío de correo activado y si el alta tiene fecha. */}
        {data?.orientacion && step < 7 && (
          <div className="alert alert--info animate-in" style={{ marginBottom: 16, fontSize: '0.88rem', lineHeight: 1.5 }}>
            <strong>Tu orientación:</strong> {data.orientacion.fecha}, a las {data.orientacion.hora}
            <div style={{ marginTop: 2 }}>{data.orientacion.lugar}</div>
          </div>
        )}

        {/* ═══ PHASE 1 — Datos ═══ */}
        {step === 1 && data && (
          <div className="card card--elevated animate-in">
            <h2 className="card__title">1. Tus Datos</h2>

            <div className="alert alert--info" style={{ marginBottom: 16, fontSize: '0.86rem' }}>
              <strong>Cómo funciona este proceso.</strong> Completa desde casa los pasos 1 a {PRESENCIAL_FROM - 1}.
              Al llegar al paso {PRESENCIAL_FROM}, el examen, detente: lo haremos juntos
              presencialmente en la orientación del martes.
            </div>

            <p style={{ color: 'var(--gray-500)', marginBottom: 20, fontSize: '0.9rem' }}>
              Confirma tus datos y completa tu información personal.
            </p>
            <div className="form-row">
              <div className="form-group"><label>Nombre</label><input className="form-input" value={data.first_name} disabled style={{ background: 'var(--gray-100)' }} /></div>
              <div className="form-group"><label>Apellidos</label><input className="form-input" value={data.last_name} disabled style={{ background: 'var(--gray-100)' }} /></div>
            </div>
            <div className="form-group">
              <label>Restaurante</label>
              <input className="form-input" value={data.restaurant_name || ''} disabled style={{ background: 'var(--gray-100)' }} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>DNI / NIE *</label>
                <input className="form-input" value={identity.dni}
                  onChange={e => setIdentity(v => ({ ...v, dni: e.target.value.toUpperCase() }))}
                  placeholder="12345678A" autoCapitalize="characters" autoCorrect="off" />
              </div>
              <div className="form-group">
                <label>Nº Seguridad Social *</label>
                <input className="form-input" value={identity.social_security_number}
                  onChange={e => setIdentity(v => ({ ...v, social_security_number: e.target.value }))}
                  placeholder="28 12345678 90" inputMode="numeric" />
                <p style={{ fontSize: '0.82rem', color: 'var(--gray-500)', margin: '6px 0 0', lineHeight: 1.45 }}>
                  ¿No tienes número de la Seguridad Social? Es obligatorio solicitarlo en la Tesorería
                  General de la Seguridad Social.{' '}
                  <a href={GUIA_NUMERO_SS} target="_blank" rel="noopener noreferrer"
                    style={{ fontWeight: 600, display: 'inline-block', padding: '2px 0' }}>
                    Consulta la guía con los pasos (PDF)
                  </a>
                </p>
              </div>
            </div>
            <div className="form-group">
              <label>IBAN (cuenta bancaria) *</label>
              <input className="form-input" value={identity.iban}
                onChange={e => setIdentity(v => ({ ...v, iban: e.target.value }))}
                placeholder="ES00 0000 0000 0000 0000 0000" />
            </div>

            <AvisoArchivos />
            <div className="form-group">
              <label>Copia de tu DNI o NIE, por las dos caras *</label>
              <p style={{ fontSize: '0.84rem', color: 'var(--gray-500)', margin: '0 0 8px 0' }}>
                Escaneado en PDF, no en foto. Puedes subir las dos caras en un solo PDF o cada
                cara en un PDF aparte.
              </p>
              {['identidad', 'identidad_2'].map(key => (
                <div key={key} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 500, marginBottom: 4 }}>
                    {key === 'identidad' ? 'Primera cara, o las dos en un archivo' : 'Segunda cara (si va aparte)'}
                  </div>
                  <input type="file" accept=".pdf,application/pdf" className="form-input" style={{ padding: 8 }}
                    onChange={e => handleFileChange(key, e.target.files[0])} />
                  {uploadFiles[key]
                    ? <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>✓ {uploadFiles[key].name}{docPaso1Subido(key) ? ' (sustituirá al anterior)' : ''}</span>
                    : docPaso1Subido(key) && <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>✓ ya adjuntado</span>}
                </div>
              ))}
            </div>
            <div className="form-group">
              <label>Certificado de titularidad de tu cuenta bancaria *</label>
              <p style={{ fontSize: '0.84rem', color: 'var(--gray-500)', margin: '0 0 8px 0' }}>
                Es el documento del banco que acredita que la cuenta del IBAN está a tu nombre,
                en PDF. Puedes descargarlo desde la web o la app de tu banco, o pedirlo en tu
                oficina y escanearlo.
              </p>
              <input type="file" accept=".pdf,application/pdf" className="form-input" style={{ padding: 8 }}
                onChange={e => handleFileChange('titularidad', e.target.files[0])} />
              {uploadFiles['titularidad']
                ? <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>✓ {uploadFiles['titularidad'].name}{docPaso1Subido('titularidad') ? ' (sustituirá al anterior)' : ''}</span>
                : docPaso1Subido('titularidad') && <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>✓ ya adjuntado</span>}
            </div>
            <div className="alert alert--info" style={{ marginBottom: 16, fontSize: '0.84rem' }}>
              <strong>Cómo escanear un documento en PDF con el móvil</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                <li><strong>iPhone:</strong> abre la app Archivos, toca el menú de los tres puntos y elige «Escanear documentos». Guárdalo en «En mi iPhone».</li>
                <li><strong>Android:</strong> abre la app Google Drive, toca el botón «+» y elige «Escanear». Después, descarga el PDF al teléfono (menú de los tres puntos, «Descargar») y súbelo desde «Descargas», no desde Drive.</li>
              </ul>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--primary btn--lg" onClick={submitPhase1} disabled={submitting}>{submitting ? 'Guardando...' : 'Siguiente →'}</button>
            </div>
          </div>
        )}

        {/* ═══ PHASE 2 — Uniformes ═══ */}
        {step === 2 && (
          <div className="card card--elevated animate-in">
            <h2 className="card__title"><Shirt size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />2. Uniformes y vacaciones</h2>
            <h4 style={{ fontSize: '0.92rem', color: 'var(--blue-800)', marginBottom: 8 }}>Uniforme</h4>
            <p style={{ color: 'var(--gray-500)', marginBottom: 16, fontSize: '0.9rem' }}>
              Accede a la web de uniformes y solicita el uniforme completo. Necesitarás pedir todas las prendas.
            </p>
            <a href="https://www.uniformes-mcd.es/" target="_blank" rel="noopener noreferrer"
              className="doc-download-card doc-download-card--action" style={{ marginBottom: 16 }}>
              <Shirt size={20} color="var(--amber-500)" />
              <div><div style={{ fontWeight: 500 }}>Web de Uniformes McDonald's</div><div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Solicita tu uniforme completo</div></div>
              <ExternalLink size={16} color="var(--gray-400)" />
            </a>
            <div className="access-check-item" onClick={() => setAccesses(a => ({ ...a, access_uniformes: !a.access_uniformes }))}>
              <div className={`access-check-box ${accesses.access_uniformes ? 'access-check-box--done' : ''}`}>
                {accesses.access_uniformes ? <Check size={16} /> : <Circle size={16} />}
              </div>
              <div><div style={{ fontWeight: 500 }}>He solicitado el uniforme completo en la web</div></div>
            </div>

            <h4 style={{ fontSize: '0.92rem', color: 'var(--blue-800)', margin: '24px 0 8px' }}>Preferencias de vacaciones</h4>
            <p style={{ color: 'var(--gray-500)', marginBottom: 12, fontSize: '0.9rem' }}>
              Indícanos en qué fechas te gustaría disfrutar tus vacaciones. Es orientativo:
              nos ayuda a cuadrar los turnos con antelación.
            </p>
            {vacationFormUrl ? (
              <a href={vacationFormUrl} target="_blank" rel="noopener noreferrer"
                className="doc-download-card doc-download-card--action" style={{ marginBottom: 12 }}>
                <FileText size={20} color="var(--amber-500)" />
                <div><div style={{ fontWeight: 500 }}>Formulario de preferencias de vacaciones</div><div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Se abre en una pestaña nueva</div></div>
                <ExternalLink size={16} color="var(--gray-400)" />
              </a>
            ) : (
              <div className="alert alert--info" style={{ marginBottom: 12, fontSize: '0.86rem' }}>
                El formulario de vacaciones estará disponible en breve. Puedes continuar
                con el resto del proceso y volver más adelante.
              </div>
            )}
            <div className="access-check-item"
              onClick={() => setAccesses(a => ({ ...a, vacation_form_done: !a.vacation_form_done }))}>
              <div className={`access-check-box ${accesses.vacation_form_done ? 'access-check-box--done' : ''}`}>
                {accesses.vacation_form_done ? <Check size={16} /> : <Circle size={16} />}
              </div>
              <div>
                <div style={{ fontWeight: 500 }}>He indicado mis preferencias de vacaciones</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button className="btn btn--secondary" onClick={() => setStep(1)}>← Atrás</button>
              <button className="btn btn--primary btn--lg" onClick={submitPhase2} disabled={submitting}>{submitting ? 'Guardando...' : 'Siguiente →'}</button>
            </div>
          </div>
        )}

        {/* ═══ PHASE 3 — Accesos (CAM + Orquest) ═══ */}
        {step === 3 && (
          <div className="card card--elevated animate-in">
            <h2 className="card__title"><Smartphone size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />3. Accesos a Plataformas</h2>

            <div className="alert alert--info" style={{ marginBottom: 20, fontSize: '0.86rem' }}>
              <strong>Recibirás dos correos:</strong> uno de <strong>Orquest</strong> y otro de
              <strong> Campus</strong>. Completa el alta en ambos siguiendo las instrucciones de
              cada correo. Si no te llegan, revisa la carpeta de spam y avísanos.
            </div>

            <h4 style={{ fontSize: '0.92rem', color: 'var(--blue-800)', marginBottom: 10 }}>Descarga las dos aplicaciones</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginBottom: 12 }}>
              Así es como se ven. Orquest está en la App Store y en Google Play; McD Connect
              se descarga desde su enlace oficial, tanto en iPhone como en Android.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {APPS.map(app => (
                <div key={app.key} style={{ flex: '1 1 240px', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-sm)', padding: 12 }}>
                  <img src={app.shot} alt={`${app.name} en la tienda de aplicaciones`}
                    style={{ width: '100%', borderRadius: 8, marginBottom: 10, display: 'block' }} />
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginBottom: 8 }}>{app.desc}</div>
                  {app.universal && (
                    <>
                      <a href={app.universal} target="_blank" rel="noopener noreferrer"
                        className="btn btn--primary btn--sm" style={{ width: '100%', justifyContent: 'center' }}>
                        Descargar {app.name}
                      </a>
                      {/* El QR solo se muestra en pantallas grandes: desde el
                          propio móvil no se puede escanear su pantalla. */}
                      <div className="solo-escritorio" style={{ textAlign: 'center', marginTop: 10 }}>
                        <img src={app.qr} alt={`QR para descargar ${app.name}`}
                          style={{ width: 140, height: 140 }} />
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                          ¿Estás en el ordenador? Escanéalo con la cámara del móvil.
                        </div>
                      </div>
                      <ol style={{ fontSize: '0.8rem', color: 'var(--gray-600)', paddingLeft: 18, margin: '10px 0 0' }}>
                        {app.pasos.map((paso, i) => <li key={i} style={{ marginBottom: 4 }}>{paso}</li>)}
                      </ol>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 6 }}>
                        Si aún no tienes tus credenciales, podrás entrar más adelante.
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {app.android && (
                      <a href={app.android} target="_blank" rel="noopener noreferrer"
                        className="btn btn--secondary btn--sm" style={{ flex: 1 }}>
                        Google Play
                      </a>
                    )}
                    {app.ios && (
                      <a href={app.ios} target="_blank" rel="noopener noreferrer"
                        className="btn btn--secondary btn--sm" style={{ flex: 1 }}>
                        App Store
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="access-check-item" style={{ marginBottom: 10 }}
              onClick={() => setAccesses(a => ({ ...a, access_orquest: !a.access_orquest }))}>
              <div className={`access-check-box ${accesses.access_orquest ? 'access-check-box--done' : ''}`}>
                {accesses.access_orquest ? <Check size={16} /> : <Circle size={16} />}
              </div>
              <div><div style={{ fontWeight: 500 }}>He completado el alta en Orquest y descargado la app</div></div>
            </div>
            <div className="access-check-item" style={{ marginBottom: 24 }}
              onClick={() => setAccesses(a => ({ ...a, access_cam: !a.access_cam }))}>
              <div className={`access-check-box ${accesses.access_cam ? 'access-check-box--done' : ''}`}>
                {accesses.access_cam ? <Check size={16} /> : <Circle size={16} />}
              </div>
              <div><div style={{ fontWeight: 500 }}>He completado el alta en Campus y descargado McD Connect</div></div>
            </div>

            <h4 style={{ fontSize: '0.92rem', color: 'var(--blue-800)', marginBottom: 8 }}>Cursos a realizar antes de la orientación</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginBottom: 12 }}>
              Dentro de Campus, completa estos tres cursos. Hacerlos ahora nos ahorra
              tiempo el día de la orientación.
            </p>
            <ul style={{ margin: '0 0 12px', paddingLeft: 20, fontSize: '0.88rem', color: 'var(--gray-700)' }}>
              {CAMPUS_COURSES.map(c => <li key={c} style={{ marginBottom: 4 }}>{c}</li>)}
            </ul>
            <a href="https://mcdcampus.sabacloud.com/" target="_blank" rel="noopener noreferrer" className="doc-download-card" style={{ marginBottom: 10 }}>
              <BookOpen size={20} color="var(--blue-500)" />
              <div><div style={{ fontWeight: 500 }}>Acceder a Campus</div></div>
              <ExternalLink size={16} color="var(--gray-400)" />
            </a>
            <div className="access-check-item"
              onClick={() => setAccesses(a => ({ ...a, courses_done: !a.courses_done }))}>
              <div className={`access-check-box ${accesses.courses_done ? 'access-check-box--done' : ''}`}>
                {accesses.courses_done ? <Check size={16} /> : <Circle size={16} />}
              </div>
              <div>
                <div style={{ fontWeight: 500 }}>He completado los tres cursos</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Márcalo cuando los tengas hechos</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button className="btn btn--secondary" onClick={() => setStep(2)}>← Atrás</button>
              <button className="btn btn--primary btn--lg" onClick={submitPhase3} disabled={submitting}>{submitting ? 'Guardando...' : 'Siguiente →'}</button>
            </div>
          </div>
        )}

        {/* ═══ PHASE 4 — Contrato, Anexo y Modelo 145 (provistos por gestoría) ═══ */}
        {step === 4 && (
          <div className="card card--elevated animate-in">
            <h2 className="card__title"><FileText size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />4. Contrato y Documentación de Gestoría</h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: 16, fontSize: '0.9rem' }}>
              Descarga cada documento que ha preparado gestoría, fírmalo en <strong>cada página</strong> y súbelo firmado.
            </p>
            <AvisoArchivos />

            <div className="alert alert--warning" style={{ marginBottom: 16, fontSize: '0.86rem' }}>
              <strong>Importante — página 6 del contrato.</strong> Antes de firmar, revisa esa
              página: debes autorizar expresamente dos cosas.
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                <li>El envío de la nómina por medios electrónicos.</li>
                <li>Las comunicaciones a través de WhatsApp.</li>
              </ul>
            </div>

            {(() => {
              const gestoriaFiles = getUploadedFiles().filter(f => f.source === 'gestoria');
              if (gestoriaFiles.length === 0) {
                return (
                  <div className="alert alert--info" style={{ marginBottom: 16 }}>
                    Tu documentación todavía no está disponible. Es normal: depende de los
                    plazos internos de gestoría y casi con toda seguridad estará subida antes
                    de la orientación del martes. <strong>Puedes continuar con el resto del
                    proceso</strong> y volver a este paso más adelante.
                  </div>
                );
              }
              return CONTRACT_DOCS.map(def => {
                // Se comprueban todas las etiquetas posibles del documento,
                // incluidas las de altas anteriores a la unificación.
                const coincide = f =>
                  def.aliases.some(a => f.label?.toLowerCase().includes(a));
                const src = gestoriaFiles.find(coincide);
                if (!src) return null;
                const signed = getUploadedFiles().find(
                  f => f.source !== 'gestoria' && coincide(f)
                );
                return (
                  <div key={def.key} style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--gray-100)' }}>
                    <a href={fileUrl(src.file)} target="_blank" rel="noreferrer" className="doc-download-card doc-download-card--action" style={{ marginBottom: 8 }}>
                      <FileText size={20} color="var(--blue-500)" />
                      <div><div style={{ fontWeight: 500 }}>{def.label}</div><div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Descargar, firmar cada página y subir</div></div>
                      <Download size={16} color="var(--gray-400)" />
                    </a>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="form-input" style={{ padding: 8 }}
                      onChange={e => handleFileChange(def.key, e.target.files[0])} />
                    {uploadFiles[def.key]
                      ? <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>
                          ✓ {uploadFiles[def.key].name}{signed ? ' (sustituirá al anterior)' : ''}
                        </span>
                      : signed && (
                        <div style={{ fontSize: '0.8rem', marginTop: 4 }}>
                          <span style={{ color: 'var(--success)' }}>
                            ✓ Ya subido: <a href={fileUrl(signed.file)} target="_blank" rel="noreferrer">ver el archivo</a>
                          </span>
                          <div style={{ color: 'var(--gray-500)', marginTop: 2 }}>
                            ¿Te has equivocado? Selecciona el correcto y pulsa «Siguiente»: sustituirá al anterior.
                          </div>
                        </div>
                      )}
                  </div>
                );
              });
            })()}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button className="btn btn--secondary" onClick={() => setStep(3)}>← Atrás</button>
              <button className="btn btn--primary btn--lg" onClick={submitPhase4} disabled={submitting}>{submitting ? 'Subiendo...' : 'Siguiente →'}</button>
            </div>
          </div>
        )}

        {/* ═══ PHASE 5 — Documentación ═══ */}
        {step === 5 && (
          <div className="card card--elevated animate-in">
            <h2 className="card__title"><FileSignature size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />5. Documentación</h2>
            <p style={{ color: 'var(--gray-500)', marginBottom: 16, fontSize: '0.9rem' }}>
              Firma los documentos de PRL. Los protocolos debes leerlos, firmarlos y subirlos; los documentos que requieren datos personales, descargarlos, rellenarlos y subirlos.
            </p>
            <AvisoArchivos />

            {/* Documentos de PRL: se firman abajo; abrirlos es opcional */}
            <h4 style={{ fontSize: '0.92rem', color: 'var(--blue-800)', marginBottom: 10 }}>Documentos de PRL para firmar</h4>
            <p style={{ fontSize: '0.84rem', color: 'var(--gray-500)', marginBottom: 10 }}>
              Firma abajo los documentos de PRL. Si quieres, puedes abrirlos o descargarlos.
            </p>
            {downloadDocs.map(d => (
              <a key={d.id} href={fileUrl(d.file)} target="_blank" rel="noreferrer" className="doc-download-card">
                <FileText size={20} color="var(--gray-500)" />
                <div><div style={{ fontWeight: 500 }}>{d.name}</div><div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Ver o descargar</div></div>
                <Download size={16} color="var(--gray-400)" />
              </a>
            ))}

            {/* Firma de los documentos de PRL (los protocolos se firman y se suben aparte) */}
            <div style={{ marginTop: 12, marginBottom: 24 }}>
              <h4 style={{ fontSize: '0.88rem', color: 'var(--blue-800)', marginBottom: 8 }}>Firma de los documentos de PRL</h4>
              <div className="signature-pad-container">
                <canvas ref={canvasRef} className="signature-canvas"
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
                <div className="signature-pad-actions">
                  {hasSigned && <button className="btn btn--ghost btn--sm" onClick={clearSignature}><Trash2 size={14} /> Borrar</button>}
                  {hasSigned && <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>✓ Firmado</span>}
                </div>
              </div>
            </div>

            {/* Documents to fill and upload (Modelo 145, Registro EPIs, etc.) */}
            <h4 style={{ fontSize: '0.92rem', color: 'var(--blue-800)', marginBottom: 10 }}>Documentos para rellenar y subir</h4>
            <p style={{ fontSize: '0.84rem', color: 'var(--gray-500)', marginBottom: 10 }}>
              Descarga cada documento, léelo o rellénalo con tus datos, fírmalo y súbelo.
            </p>
            {docsForPhase5.map(d => (
              <div key={d.id} style={{ marginBottom: 16 }}>
                <a href={fileUrl(d.file)} target="_blank" rel="noreferrer" className="doc-download-card doc-download-card--action">
                  <Upload size={20} color="var(--amber-500)" />
                  <div><div style={{ fontWeight: 500 }}>{d.name}</div><div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{d.name.toLowerCase().includes('protocolo') ? 'Leer, firmar y subir' : 'Descargar, rellenar y subir'}</div></div>
                  <Download size={16} color="var(--gray-400)" />
                </a>
                <UploadedBadge docId={d.id} />
                {/* El campo se mantiene aunque ya esté subido: si el empleado
                    se ha equivocado, sube el correcto y sustituye al anterior. */}
                <div style={{ paddingLeft: 8, marginTop: 6 }}>
                  {isDocUploaded(d.id) && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: 4 }}>
                      ¿Te has equivocado? Selecciona el archivo correcto y pulsa «Siguiente»: sustituirá al anterior.
                    </div>
                  )}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="form-input" style={{ padding: 8 }}
                    onChange={e => handleFileChange(`doc_${d.id}`, e.target.files[0])} />
                  {uploadFiles[`doc_${d.id}`] && <span style={{ fontSize: '0.8rem', color: 'var(--success)' }}>✓ {uploadFiles[`doc_${d.id}`].name}{isDocUploaded(d.id) ? ' (sustituirá al anterior)' : ''}</span>}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button className="btn btn--secondary" onClick={() => setStep(4)}>← Atrás</button>
              <button className="btn btn--primary btn--lg" onClick={submitPhase5} disabled={submitting}>{submitting ? 'Subiendo...' : 'Siguiente →'}</button>
            </div>
          </div>
        )}

        {/* ═══ PHASE 6 — Examen Manipulador ═══ */}
        {step === 6 && (
          <div className="card card--elevated animate-in">
            <h2 className="card__title"><ClipboardCheck size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />6. Examen Manipulador de Alimentos</h2>
            <PresencialNotice />
            <p style={{ color: 'var(--gray-500)', marginBottom: 20, fontSize: '0.9rem' }}>
              Completa los dos exámenes. Marca V (Verdadero) o F (Falso) para cada afirmación.
            </p>

            <ExamForm
              title="Examen Nº 1 — Orientación Inicial (63 preguntas)"
              questions={EXAM1_QUESTIONS}
              requiredScore={60}
              totalQuestions={63}
              previousScore={data?.exam1_score}
              previousPassed={data?.exam1_passed}
              onSubmit={(r) => setExamResults(prev => ({ ...prev, exam1: r }))}
            />

            {(examResults.exam1?.passed || data?.exam1_passed) && (
              <div style={{ marginTop: 20 }}>
                <ExamForm
                  title="Examen Nº 2 — Orientación Seguimiento (20 preguntas)"
                  questions={EXAM2_QUESTIONS}
                  requiredScore={19}
                  totalQuestions={20}
                  previousScore={data?.exam2_score}
                  previousPassed={data?.exam2_passed}
                  onSubmit={(r) => setExamResults(prev => ({ ...prev, exam2: r }))}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button className="btn btn--secondary" onClick={() => setStep(5)}>← Atrás</button>
              <button className="btn btn--primary btn--lg" onClick={submitPhase6} disabled={submitting || !(examResults.exam1?.passed || data?.exam1_passed) || !(examResults.exam2?.passed || data?.exam2_passed)}>
                {submitting ? 'Guardando...' : 'Siguiente →'}
              </button>
            </div>
          </div>
        )}

        {/* ═══ PASO 7 — Primer día, con la PRL ═══ */}
        {step === 7 && (
          <div className="card card--elevated animate-in">
            <h2 className="card__title"><Building size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />7. Tu Primer Día</h2>

            <div className="alert alert--info" style={{ marginBottom: 20, fontSize: '0.86rem' }}>
              <strong>Curso de Prevención de Riesgos Laborales (PRL).</strong> Es obligatorio y
              tienes una semana para hacerlo. Te recomendamos usar Google Chrome o Mozilla Firefox.
              <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                <li>
                  Entra en{' '}
                  <a href="https://mcdonaldsfranquiciados.smartlearning.global/" target="_blank" rel="noreferrer">
                    https://mcdonaldsfranquiciados.smartlearning.global/
                  </a>
                </li>
                <li>
                  Accede con estos datos:
                  <div>Nombre de usuario: <strong>{data?.email ? data.email.toLowerCase() : 'el correo electrónico que nos diste, en minúsculas'}</strong></div>
                  <div>Contraseña: <strong>{(data?.dni || identity.dni || '').toLowerCase()}</strong></div>
                </li>
                <li>Si lo deseas, puedes cambiar la contraseña desde tu perfil o con la opción «¿Has olvidado tu contraseña?».</li>
              </ol>
              <div style={{ marginTop: 8 }}>
                Al terminar, descarga el certificado y envíalo a{' '}
                <a href="mailto:sae@esmcd.com">sae@esmcd.com</a>. Si tienes dudas o no puedes
                acceder, escríbenos a esa misma dirección.
                {data?.email && ' También te enviamos estas instrucciones por correo.'}
              </div>
            </div>

            <div style={{ background: 'var(--blue-50)', padding: 16, borderRadius: 'var(--radius-sm)', marginBottom: 20, border: '1px solid var(--blue-200)' }}>
              <h4 style={{ color: 'var(--blue-800)', marginBottom: 8 }}>Tu restaurante: {data?.restaurant_name}</h4>
              {data?.restaurant_address && (
                <p style={{ fontSize: '0.88rem', color: 'var(--gray-600)', margin: '0 0 8px 0' }}>
                  📍 <strong>{data.restaurant_address}</strong>
                </p>
              )}
              <p style={{ fontSize: '0.88rem', color: 'var(--gray-600)', margin: 0 }}>
                Consulta tu horario en <strong>Orquest</strong> (o pregunta en la orientación). El encargado de turno te recibirá.
              </p>
            </div>

            <div style={{ background: 'var(--warning-light)', padding: 16, borderRadius: 'var(--radius-sm)', marginBottom: 20, border: '1px solid var(--warning)' }}>
              <h4 style={{ color: 'var(--gray-800)', marginBottom: 8 }}>¿Qué llevar?</h4>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.88rem', color: 'var(--gray-700)' }}>
                <li style={{ marginBottom: 4 }}><strong>Tu uniforme completo</strong> (el que solicitaste en la fase 2)</li>
                <li><strong>Zapatos completamente negros</strong> (cerrados, antideslizantes)</li>
              </ul>
            </div>

            <h4 style={{ fontSize: '0.92rem', color: 'var(--blue-800)', marginBottom: 12 }}>Resumen de tu alta</h4>
            <SummaryChecklist />

            {guideDocs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: '0.92rem', color: 'var(--blue-800)', marginBottom: 10 }}>Guía para nuevos empleados</h4>
                {guideDocs.map(d => (
                  <a key={d.id} href={fileUrl(d.file)} target="_blank" rel="noreferrer" className="doc-download-card">
                    <BookOpen size={20} color="var(--blue-500)" />
                    <div><div style={{ fontWeight: 500 }}>{d.name}</div></div>
                    <Download size={16} color="var(--gray-400)" />
                  </a>
                ))}
              </div>
            )}

            <div className="access-check-item" onClick={() => setPhase8(p => ({ guide_read: !p.guide_read }))}>
              <div className={`access-check-box ${phase8.guide_read ? 'access-check-box--done' : ''}`}>
                {phase8.guide_read ? <Check size={16} /> : <Circle size={16} />}
              </div>
              <div><div style={{ fontWeight: 500 }}>He leído la Guía para Nuevos Empleados</div></div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button className="btn btn--secondary" onClick={() => setStep(6)}>← Atrás</button>
              <button className="btn btn--primary btn--lg" onClick={submitPhase8} disabled={submitting}>
                <Send size={16} /> {submitting ? 'Finalizando...' : 'Finalizar proceso'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
